import { jsx } from 'react/jsx-runtime';
import { useContext, createContext, Component, useMemo, useState, Fragment } from 'react';

/**
 * Variable checking the server environment runtime.
 */ const IS_SERVER_ENVIRONMENT = typeof window === "undefined";
/**
 * Variable to detect the test environment.
 * Testing runtimes (Jest and Vitest) automatically set the `NODE_ENV` environment variable to `test`.
 * Thanks to the `DefinePlugin` setup, the bundler minifier automatically strips the conditional code statement if NODE_ENV !== "test".
 * @see {@link https://vitest.dev/guide/migration.html#envs Node environment injection}.
 */ process.env.NODE_ENV === "test";
const LANGUAGE_TAGS_VALUES = [
    "de-DE",
    "de-AT",
    "en-GB",
    "fr-BE",
    "fr-FR",
    "nl-BE"
];
new Set(LANGUAGE_TAGS_VALUES);
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[0]));
new Set(LANGUAGE_TAGS_VALUES.map((tag)=>tag.split("-")[1]));
/**
 * ErrorBoundary component to catch errors in children components.
 * @see {@link https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary ErrorBoundary}
 */ class ErrorBoundary extends Component {
    constructor(props){
        super(props);
        this.state = {
            hasError: false
        };
    }
    handleError(...input) {
        const log = this.props.onError ?? console.error;
        log(...input);
    }
    static getDerivedStateFromError() {
        return {
            hasError: true
        };
    }
    componentDidCatch(error, errorInfo) {
        this.handleError(error, {
            id: this.props.id,
            errorInfo
        });
    }
    render() {
        const { fallback: Fallback, children } = this.props;
        const { hasError } = this.state;
        if (hasError && Fallback) {
            return /*#__PURE__*/ jsx(Fallback, {});
        }
        /**
     * The ErrorBoundary component only catches client-side errors and does not handle errors during server-side rendering (SSR) with `renderToString`.
     * To mitigate this, since React v18 and following the [server errors RFC](https://github.com/reactjs/rfcs/blob/ba9bd5744cb922184ec9390515910cd104a30c6e/text/0215-server-errors-in-react-18.md),
     * to recover from errors thrown on the server side, the React tree is wrapped with a suspense to display, by default, an error fallback in case of unmanaged rendering error or unhandled lazy behaviors consumer side.
     * When an error is thrown on the server, React will emit the fallback HTML from the server and then automatically retry rendering on the client.
     *
     * Although this approach works, it causes hydration errors
     * (e.g., "Uncaught Error: This Suspense boundary received an update before it finished hydrating").
     * @see {@link https://github.com/reactjs/rfcs/blob/ba9bd5744cb922184ec9390515910cd104a30c6e/text/0215-server-errors-in-react-18.md Server errors RFC}
     * @see {@link https://react.dev/reference/react-dom/server/renderToString#caveats renderToString caveats}
     */ return children;
    }
}
/**
 * A factory to format an error message in a consistent way.
 * @param configuration The configuration object.
 * @returns The formatted error message.
 */ const createErrorMessage = ({ cause, solution })=>{
    return `❌ An error occurred: ${cause}.\n✅ To solve the issue: ${solution}.`;
};
/**
 * A factory to uniformize the way global module variables are named.
 * It prefixes each module by the Unified Frontend {@link https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend_registry/blob/6d544d0fb00088c7af670924bb25150128a45285/libraries/registry/types/index.ts#L139 capability code}.
 * @param module - The module name.
 * @param component - Optional component name to append.
 * @returns The global key name derivated from the input (used to scope global states linked to a module).
 */ const createGlobalKey = (module, component)=>{
    return `__UFRN_${module.toUpperCase()}${!component ? "" : `_${component.toUpperCase()}`}__`;
};
createGlobalKey("BUILD_FINGERPRINTS");
const IsIntantiatedContext = /*#__PURE__*/ createContext(false);
/**
 * A higher-order-factory-like helper to enforce a single component instance in the tree.
 * @param componentName The component name.
 * @param Component The targetted React component to decorate with the singleton behavior (most of the time, Provider-like component).
 * @returns The decorated component with singleton behavior.
 */ const createSingletonComponent = (componentName, Component)=>{
    return (props)=>{
        const isInstantiated = useContext(IsIntantiatedContext);
        if (isInstantiated) {
            console.error(createErrorMessage({
                cause: `The \`${componentName}\` component cannot be instantiated more than once`,
                solution: `The singleton behavior has been automatically enforced by skipping the nested \`${componentName}\` element creation. To remove the log and prevent any introduced side effect(s), please remove any \`${componentName}\` component instance in your component tree outside the sandbox entry point.\nIf the log still persists, make sure the sandbox entry point is not included in your production bundle`
            }));
            return props.children;
        }
        return /*#__PURE__*/ jsx(IsIntantiatedContext.Provider, {
            value: true,
            children: /*#__PURE__*/ jsx(Component, {
                ...props
            })
        });
    };
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getComponentName = (input)=>input.displayName || input.name || "Component";
/**
 * Get a state value stored globally depending on the runtime (server vs. client runtime).
 *
 * @param key The global store key.
 * @param fallbackValue A fallback value in case no store value is defined globally (used server side as a value source of truth).
 * @returns The found state value (always defined).
 */ const getGlobalStoreValue = (key, fallbackValue)=>{
    if (IS_SERVER_ENVIRONMENT) return fallbackValue;
    const states = Object.entries(fallbackValue);
    const hydrationSource = window[key];
    const output = {};
    for (const [stateKey, stateValue] of states){
        output[stateKey] = hydrationSource?.[stateKey] ?? stateValue;
    }
    return output;
};
/**
 * Serialize a Json object to string.
 *
 * It's a safer `JSON.stringify` alternative to prevent XSS vulnerabilities
 * during the HTML parsing phase by escaping all HTML-like tag syntax.
 *
 * @param input The JSON object.
 * @returns Safe stringified object representation.
 */ const serializeJson = (input)=>{
    return JSON.stringify(escapeHtml(JSON.stringify(input)));
};
/**
 * Escapes special characters and HTML entities in a given html string
 * Source of inspiration: https://github.com/vercel/next.js/blob/e2584c6fb3011f5150c28563b2c301eae5b290fd/packages/next/src/server/htmlescape.ts#L1
 * @param   content HTML string to escape for later insertion
 * @returns Escaped content
 */ const escapeHtml = (content)=>{
    return content.replace(ESCAPE_REGEX, (match)=>ESCAPE_LOOKUP[match]);
};
const ESCAPE_LOOKUP = {
    "&": "\\u0026",
    ">": "\\u003e",
    "<": "\\u003c",
    "\u2028": "\\u2028",
    "\u2029": "\\u2029"
};
const ESCAPE_REGEX = /[&><\u2028\u2029]/g;
/**
 * Serialize a record-like global store data to string.
 * @param key The global key.
 * @param value The record-like value.
 * @returns A serialized (string) script.
 */ const serializeGlobalStoreScript = (key, value)=>`<script>window["${key}"]=JSON.parse(${serializeJson(value)});</script>`;
/**
 * A React hook to enforce the Provider definition before using a context.
 * @param scope Metadata describing the context scope (including package and module name).
 * @param context The React `Context` to check.
 * @returns The context value.
 */ const useSafeContext = (scope, context)=>{
    const value = useContext(context);
    if (value === null) {
        // Apply the fail-fast principle to prevent any issues in production (e.g. the ones with default values
        // extraneous the current deployment target (such as applying `AVIV_THEME` while the current brand is SeLoger))
        throw new Error(createErrorMessage({
            cause: `The \`${scope.module}\` module is not able to retrieve contextual data`,
            solution: `Verify your Shell integration by checking if the \`${scope.package}\` package is effectively shared.\nIf the issue is only reproducible in your local environment, make sure to simulate the Shell environment with the right component(s)`
        }));
    }
    return value;
};

const useRuntimeContext = (moduleName, context)=>useSafeContext({
        package: "@aviv-unified-frontend/runtime",
        module: moduleName
    }, context);

const GLOBAL_KEY = createGlobalKey("lifecycle", "serverRequest");
const INTERNAL_LIFECYCLE_KEY = Symbol(GLOBAL_KEY);
const ServerRequestContext = /*#__PURE__*/ createContext(null);
/**
 * Create context for ServerRequestProvider.
 */ const createServerRequestContext = ({ id, store })=>{
    const serverRequestStore = getGlobalStoreValue(GLOBAL_KEY, {
        [id]: store
    });
    return {
        [INTERNAL_LIFECYCLE_KEY]: {
            id,
            store: serverRequestStore
        },
        getScript: ()=>{
            return serializeGlobalStoreScript(GLOBAL_KEY, serverRequestStore);
        }
    };
};
const ServerRequestProvider = ({ children, context })=>{
    const { id, store } = context[INTERNAL_LIFECYCLE_KEY];
    const value = useMemo(()=>{
        return store[id];
    }, [
        id,
        store
    ]);
    return /*#__PURE__*/ jsx(ServerRequestContext.Provider, {
        value: value,
        children: children
    });
};
const useServerRequestContext = ()=>useRuntimeContext("lifecycle", ServerRequestContext);

const LifecycleContextInput = /*#__PURE__*/ createContext(null);
/**
 * Create the lifecycle context.
 * @returns The lifecycle context instance with the needed methods to manage it.
 * @description ⚠️ Function meant to be consumed by the shell only.
 */ const createLifecycleContext = (context)=>context;
const defaultUseOverlayTrigger = ()=>({
        canDisplay: true
    });
const defaultContext = createLifecycleContext({
    onInitialization: ()=>{},
    useOverlayTrigger: defaultUseOverlayTrigger
});
/**
 * LifecycleProvider
 * @description ⚠️ Function meant to be consumed by the shell only.
 */ const LifecycleProvider = createSingletonComponent("LifeCycleProvider", ({ context = defaultContext, children })=>{
    const { FallbackComponent, onInitialization, useOverlayTrigger } = context;
    const value = useMemo(()=>{
        return {
            FallbackComponent,
            onInitialization,
            useOverlayTrigger: useOverlayTrigger ?? defaultUseOverlayTrigger
        };
    }, [
        FallbackComponent,
        onInitialization,
        useOverlayTrigger
    ]);
    return /*#__PURE__*/ jsx(LifecycleContextInput.Provider, {
        value: value,
        children: children
    });
});
const useLifecycleContext = ()=>{
    return useRuntimeContext("lifecycle", LifecycleContextInput);
};

const createComponent = ({ id, Component, FallbackComponent, ServerRequestProvider, logger, version })=>{
    const WrappedComponent = (props)=>{
        const { FallbackComponent: DefaultFallbackComponent, onInitialization } = useLifecycleContext();
        // Leverage `useState` callback form to run only once the `onInitialization` callback as soon as possible on both runtime (server and client):
        useState(()=>{
            onInitialization({
                id,
                version
            });
        });
        const ComponentProvider = ServerRequestProvider ?? Fragment;
        return /*#__PURE__*/ jsx(ComponentProvider, {
            children: /*#__PURE__*/ jsx(ErrorBoundary, {
                id: id,
                onError: logger?.error,
                fallback: FallbackComponent ?? DefaultFallbackComponent,
                children: /*#__PURE__*/ jsx(Component, {
                    ...props
                })
            })
        });
    };
    WrappedComponent.displayName = `CreateLifecycleComponent(${getComponentName(Component)})`;
    return WrappedComponent;
};

const createApplication = ({ id, Component, FallbackComponent, logger, onServerRequest, version })=>{
    let context = undefined;
    const getLifecycleContext = ()=>{
        return context ?? createServerRequestContext({
            id
        });
    };
    return {
        Component: createComponent({
            id,
            Component,
            FallbackComponent,
            ServerRequestProvider: ({ children })=>{
                return /*#__PURE__*/ jsx(ServerRequestProvider, {
                    context: getLifecycleContext(),
                    children: children
                });
            },
            logger,
            version
        }),
        handleServerRequest: IS_SERVER_ENVIRONMENT ? onServerRequest : undefined,
        setLifecycleContext (input) {
            context = createServerRequestContext(input);
        },
        getLifecycleContext,
        version
    };
};

const createMicroFrontend = ({ id, Component, FallbackComponent, logger, version })=>{
    return createComponent({
        id,
        Component,
        FallbackComponent,
        logger,
        version
    });
};

/**
 * This hook is designed to be used in combination with lifecycle factories (such as `createApplication`)
 * and the `onServerRequest` callback function. It allows accessing to
 * the `data` returned by the `onServerRequest` callback function.
 */ const useServerRequest = ()=>{
    const contextValue = useServerRequestContext();
    return useMemo(()=>{
        const data = contextValue?.data;
        if (!data) {
            return undefined;
        }
        return {
            data
        };
    }, [
        contextValue
    ]);
}; /**
 * Currently, the provided data is static because it is only fetched during the server-side request.
 * If, in the future, client-side fetching logic is required, the current implementation can be adapted to leverage the mechanisms of useFetcher for this purpose.
 * https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend/pull/742#discussion_r1711750846
 */  // export const useServerRequest = () => {
 //   const { id, useFetcher, serverResquestData } = useLifecycleContext();
 //   const {
 //     data,
 //     /* error, ..., */ // Later, other fetcher fields can be exposed more easily without reinventing the wheel to manage promise resolution for both lifecycle and fetcher cases.
 //   } = useFetcher(
 //     createServerRequestKey(id),
 //     () => {
 //       return Promise.resolve(serverResquestData);
 //     },
 //     {
 //       prefetching: true,
 //       strategy: "cache-first", // We can have a new strategy `cache-only` that will benefit not only `useServerRequest` but also `useFetcher` direct usage
 //     },
 //   );
 //   return data; // no useMemo needed, already optimized to be triggered at the right moment
 // }

const useOverlayTrigger = ()=>{
    const { useOverlayTrigger } = useLifecycleContext();
    const { canDisplay } = useOverlayTrigger();
    return useMemo(()=>({
            canDisplay
        }), [
        canDisplay
    ]);
};

export { LifecycleProvider, createApplication, createLifecycleContext, createMicroFrontend, useOverlayTrigger, useServerRequest };
